<script setup>
const { locale, t } = useI18n();
const route = useRoute();
const fundSearch = ref('');

import { pageTransition } from '~/helpers/animations/animations';

definePageMeta({
  pageTransition: pageTransition,
});

const pageQuery = gql`
  query GetPageData($routeId: String!) {
    page(route_id: $routeId) {
      breadcrumbs {
        id
        name
        route_id
        slug: seo_url_slug {
          cs
          en
        }
      }
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      name
      content
    }
    documents(types: ["fond"]) {
      name
      file {
        url
      }
      fond_bm
      fond_mark
      fond_time_range
      fond_description
      section {
        name
      }
    }
  }
`;

const { data } = await useAsyncQuery(pageQuery, {
  routeId: route.name,
});

const page = computed(() => data.value?.page);
const funds = computed(() => data.value?.documents);

const checkAboutPageRoute = (path) => {
  if (path.slug[locale] == route.path) {
    return null;
  } else if (path.route_id == null) {
    return useGetRoute(path, page.value?.breadcrumbs[0].route_id);
  } else {
    return useGetRoute(path).name;
  }
};

const breadcrumbs = computed(() => {
  return page.value && page.value?.breadcrumbs
    ? [...page.value?.breadcrumbs].map((x, key) => ({
        title: x.name,
        route: key + 1 == page.value?.breadcrumbs.length ? null : checkAboutPageRoute(x),
      }))
    : [];
});

useHeadData({
  title: page.value?.seo_meta_title || page.value?.name || t('pages.funds.index.breadcrumbs-title'),
  description: page.value?.seo_meta_description || null,
  keywords: page.value?.seo_meta_keywords || null,
  image: page.value?.image || page.value?.cropped_detail_image || null,
  url: route.fullPath,
});

const fundCategories = computed(() => {
  let fundCategories = [];

  funds.value.forEach((fund) => {
    fundCategories.push(fund.section.name);
  });

  // Get all unique values in a JavaScript array (remove duplicates)
  fundCategories = fundCategories.filter((a, b, array) => array.indexOf(a) === b);

  // Sort alphabetically
  fundCategories = fundCategories.sort((a, b) => a.localeCompare(b));

  return fundCategories;
});

const filteredFunds = computed(() => {
  return funds.value.filter((item) => {
    return filterSearch(item.name) || filterSearch(item.fond_mark) || filterSearch(item.fond_description);
  });
});

const searchUnite = (text) => {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

const filterSearch = (name) => {
  return name ? searchUnite(name).indexOf(searchUnite(fundSearch.value)) > -1 : null;
};
</script>

<template>
  <main class="main">
    <!-- <Breadcrumbs :items="breadcrumbs" /> -->
    <div v-if="page">
      <h1 class="heading">{{ page.name }}</h1>
      <DynamicPageContent v-if="page.content" :content="page.content" class="mt-5" />
      <div class="mt-4">
        <label for="search" class="hidden">{{ t('pages.funds.search') }}</label>
        <input id="search" type="text" class="input input--search" :placeholder="t('pages.funds.search')" v-model="fundSearch" />
        <a @click.prevent="fundSearch = ''" class="link clear-filter" v-if="fundSearch != ''">{{ t('global.clear-filter') }}</a>
      </div>

      <template v-for="(fundCategory, index) in fundCategories" :key="index">
        <h2 class="heading heading--2 heading--thin mt-5">{{ fundCategory }}</h2>

        <div class="container-xl ml-0 pl-0">
          <div class="table mt-4">
            <table class="table__elem">
              <thead class="table__head">
                <tr class="table__row">
                  <th class="table__header">{{ t('pages.funds.name') }}</th>
                  <th class="table__header">{{ t('pages.funds.fond_mark') }}</th>
                  <th class="table__header">{{ t('pages.funds.fond_time_range') }}</th>
                  <th class="table__header">{{ t('pages.funds.fond_bm') }}</th>
                  <th class="table__header">{{ t('pages.funds.fond_description') }}</th>
                </tr>
              </thead>
              <tbody class="table__body">
                <tr class="table__row" v-for="(fund, index) in filteredFunds" :key="index">
                  <template v-if="fund.section.name == fundCategory">
                    <td class="table__cell">
                      <a v-if="fund.file" class="text text--3 upper" :href="fund.file.url" target="_blank">{{ fund.name }}</a>
                      <span v-else class="text text--3 upper">{{ fund.name }}</span>
                    </td>
                    <td class="table__cell">
                      <span class="text text--3 upper">{{ fund.fond_mark }}</span>
                    </td>
                    <td class="table__cell">
                      <span class="text text--3 upper">{{ fund.fond_time_range }}</span>
                    </td>
                    <td class="table__cell">
                      <span class="text text--3 upper">{{ fund.fond_bm }}</span>
                    </td>
                    <td class="table__cell">
                      <span class="text text--3">{{ fund.fond_description }}</span>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.clear-filter {
  display: inline-block;
  margin-left: 15px;
  margin-top: 15px;
}
</style>
